import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPlus,
    faTimes,
    faSearch,
    faLayerGroup,
    faFolder,
    faFolderOpen,
    faArrowDownWideShort,
    faGrip,
    faTableCells,
    faTableCellsLarge,
    faFilter,
    faChevronDown,
    faChevronUp
} from "@fortawesome/free-solid-svg-icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Badge from "react-bootstrap/Badge";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import ListGroup from "react-bootstrap/ListGroup";
import Stack from "react-bootstrap/Stack";
import Dropdown from "react-bootstrap/Dropdown";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Button from "react-bootstrap/Button";

import { AppLayout } from "../components/appLayout";
import useApiClient from "../hooks/useApiClient";
import {
    CategoryWithCountsViewModel,
    SearchVideosOptionsBindingModel,
    SortDirection,
    TagWithVideoCountViewModel,
    ToggleVideoTagBindingModel,
    VideoSortType,
    VideoViewModel,
    VideoViewModelPaginatedResultsViewModel
} from "../apiClient";
import { VideoResults } from "../components/videoResults";
import { AddCategoryModal } from "../components/AddCategoryModal";
import { useQuickAdd } from "../contexts/QuickAddContext";

interface SortOption {
    label: string;
    sortType: VideoSortType;
    sortDirection: SortDirection;
}

const SORT_OPTIONS: SortOption[] = [
    { label: "Recently Updated", sortType: VideoSortType._4, sortDirection: SortDirection._1 },
    { label: "Published: Newest", sortType: VideoSortType._3, sortDirection: SortDirection._1 },
    { label: "Published: Oldest", sortType: VideoSortType._3, sortDirection: SortDirection._0 },
    { label: "Title: A to Z", sortType: VideoSortType._2, sortDirection: SortDirection._0 },
    { label: "Title: Z to A", sortType: VideoSortType._2, sortDirection: SortDirection._1 },
    { label: "Duration: Longest", sortType: VideoSortType._0, sortDirection: SortDirection._1 },
    { label: "Duration: Shortest", sortType: VideoSortType._0, sortDirection: SortDirection._0 },
    { label: "Provider", sortType: VideoSortType._1, sortDirection: SortDirection._0 },
];

const getSearchOptionsBindingModelFromUrlSearchParams = (urlSearchParams: URLSearchParams): SearchVideosOptionsBindingModel => {
    const categoryName = urlSearchParams.get('categoryName');
    const page = urlSearchParams.get('page') ? parseInt(urlSearchParams.get('page')!, 10) : 1;
    const query = urlSearchParams.get('query') ?? null;
    const tags = urlSearchParams.getAll('tag');
    const sortTypeParam = urlSearchParams.get('sortType');
    const sortDirParam = urlSearchParams.get('sortDirection');

    const sortType = sortTypeParam != null && sortTypeParam !== ''
        ? parseInt(sortTypeParam, 10)
        : VideoSortType._4;
    const sortDirection = sortDirParam != null && sortDirParam !== ''
        ? parseInt(sortDirParam, 10)
        : SortDirection._1;

    return new SearchVideosOptionsBindingModel({
        categoryName: categoryName || null,
        page,
        pageSize: 40,
        query,
        tags,
        sortType,
        sortDirection
    });
};

export const Home = () => {
    const [urlSearchParams, setUrlSearchParams] = useSearchParams();
    const setUrlSearchParamsRef = useRef(setUrlSearchParams);
    setUrlSearchParamsRef.current = setUrlSearchParams;

    const apiClient = useApiClient();
    const { registerRefreshHandler } = useQuickAdd();
    const [categories, setCategories] = useState<CategoryWithCountsViewModel[]>([]);
    const [videoPaginatedResults, setVideoPaginatedResults] = useState<VideoViewModelPaginatedResultsViewModel | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [searchVideosOptions, setSearchVideosOptions] = useState<SearchVideosOptionsBindingModel>(() =>
        getSearchOptionsBindingModelFromUrlSearchParams(urlSearchParams)
    );
    const [tags, setTags] = useState<TagWithVideoCountViewModel[]>([]);
    const [categorySearchQuery, setCategorySearchQuery] = useState<string>('');
    const [showAddCategoryModal, setShowAddCategoryModal] = useState<boolean>(false);
    const [isCategoriesExpanded, setIsCategoriesExpanded] = useState<boolean>(false);

    const [columns, setColumns] = useState<number>(() => {
        try {
            const saved = localStorage.getItem('vl_grid_columns');
            if (saved) {
                const parsed = parseInt(saved, 10);
                if ([3, 4, 6].includes(parsed)) return parsed;
            }
        } catch {
            // ignore
        }
        return 4;
    });

    const handleColumnsChange = (cols: number) => {
        setColumns(cols);
        try {
            localStorage.setItem('vl_grid_columns', cols.toString());
        } catch {
            // ignore
        }
    };

    const syncUrl = useCallback((options: SearchVideosOptionsBindingModel) => {
        const params = new URLSearchParams();
        if (options.categoryName) params.set('categoryName', options.categoryName);
        if (options.page && options.page > 1) params.set('page', options.page.toString());
        if (options.query) params.set('query', options.query);
        options.tags?.forEach(t => params.append('tag', t));
        if (options.sortType != null && options.sortType !== VideoSortType._4) {
            params.set('sortType', options.sortType.toString());
        }
        if (options.sortDirection != null && options.sortDirection !== SortDirection._1) {
            params.set('sortDirection', options.sortDirection.toString());
        }
        setUrlSearchParamsRef.current(params, { replace: true });
    }, []);

    const updateSearchOptions = useCallback((updater: (prev: SearchVideosOptionsBindingModel) => SearchVideosOptionsBindingModel) => {
        setSearchVideosOptions((prev) => {
            const next = updater(prev);
            syncUrl(next);
            return next;
        });
    }, [syncUrl]);

    const loadData = useCallback(async () => {
        if (!apiClient) return;
        setLoading(true);
        try {
            const [allCats, allTags, videoResults] = await Promise.all([
                apiClient.categoriesAll ? apiClient.categoriesAll() : Promise.resolve([]),
                apiClient.tagsAll ? apiClient.tagsAll() : Promise.resolve([]),
                apiClient.search ? apiClient.search(searchVideosOptions) : Promise.resolve(null),
            ]);
            setCategories(allCats ?? []);
            setTags(allTags ?? []);
            if (videoResults) {
                setVideoPaginatedResults(videoResults);
            }
        } finally {
            setLoading(false);
        }
    }, [apiClient, searchVideosOptions]);

    const handleSelectCategory = (categoryName: string | null) => {
        updateSearchOptions((prev) => new SearchVideosOptionsBindingModel({
            ...prev,
            categoryName: prev.categoryName === categoryName ? null : categoryName,
            page: 1
        }));
    };

    const handlePageChange = (pageNumber: number) => {
        updateSearchOptions((prev) => new SearchVideosOptionsBindingModel({
            ...prev,
            page: pageNumber
        }));
    };

    const handleRemoveTagFromSearchOptions = (tag: string) => {
        updateSearchOptions((prev) => new SearchVideosOptionsBindingModel({
            ...prev,
            tags: prev.tags?.filter(t => t !== tag) ?? [],
            page: 1
        }));
    };

    const handleRemoveQuery = () => {
        updateSearchOptions((prev) => new SearchVideosOptionsBindingModel({
            ...prev,
            query: null,
            page: 1
        }));
    };

    const handleClearCategoryFilter = () => {
        updateSearchOptions((prev) => new SearchVideosOptionsBindingModel({
            ...prev,
            categoryName: null,
            page: 1
        }));
    };

    const handleClearAllFilters = () => {
        updateSearchOptions((prev) => new SearchVideosOptionsBindingModel({
            ...prev,
            categoryName: null,
            query: null,
            tags: [],
            page: 1
        }));
    };

    const handleSortChange = (option: SortOption) => {
        updateSearchOptions((prev) => new SearchVideosOptionsBindingModel({
            ...prev,
            sortType: option.sortType,
            sortDirection: option.sortDirection,
            page: 1
        }));
    };

    const handleVideoToggleTag = async (video: VideoViewModel, bindingModel: ToggleVideoTagBindingModel) => {
        try {
            const result = await apiClient!.tags(video.id!, bindingModel);
            const updatedResults = new VideoViewModelPaginatedResultsViewModel({ ...videoPaginatedResults });
            const videoIndex = updatedResults.results?.findIndex(v => v.id === video.id) ?? -1;

            if (searchVideosOptions.tags?.length !== 0 && !result.tags?.some(t => searchVideosOptions.tags?.includes(t))) {
                updatedResults.results?.splice(videoIndex, 1);
            } else if (videoIndex !== -1 && updatedResults.results) {
                updatedResults.results[videoIndex] = result;
            }

            setVideoPaginatedResults(updatedResults);
            if (apiClient?.tagsAll) {
                setTags(await apiClient.tagsAll());
            }
        } catch (err) {
            console.error(err);
            loadData();
        }
    };

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        const unregister = registerRefreshHandler('videos', loadData);
        return () => unregister();
    }, [registerRefreshHandler, loadData]);

    useEffect(() => {
        const query = urlSearchParams.get('query');
        if (query && query.length > 0 && query !== searchVideosOptions.query) {
            setSearchVideosOptions((prev) => new SearchVideosOptionsBindingModel({
                ...prev,
                query
            }));
        }
    }, [urlSearchParams, searchVideosOptions.query]);

    const totalCategoryVideos = useMemo(() => {
        return categories.reduce((sum, c) => sum + (c.videoCount ?? 0), 0);
    }, [categories]);

    const filteredCategories = useMemo(() => {
        const query = categorySearchQuery.trim().toLowerCase();
        if (!query) return categories;
        return categories.filter(c => c.name?.toLowerCase().includes(query));
    }, [categories, categorySearchQuery]);

    const currentSort = useMemo(() => {
        return SORT_OPTIONS.find(
            o => o.sortType === searchVideosOptions.sortType && o.sortDirection === searchVideosOptions.sortDirection
        ) ?? SORT_OPTIONS[0];
    }, [searchVideosOptions.sortType, searchVideosOptions.sortDirection]);

    const hasActiveFilters = Boolean(
        searchVideosOptions.categoryName ||
        searchVideosOptions.query ||
        (searchVideosOptions.tags && searchVideosOptions.tags.length > 0)
    );

    return (
        <AppLayout loading={loading}>
            <Container fluid>
                <Row className="g-3">
                    <Col lg={3} xl={2}>
                        <Card className={`category-sidebar ${isCategoriesExpanded ? 'category-sidebar--expanded' : 'category-sidebar--collapsed'}`}>
                            <Card.Header className="category-sidebar__header d-flex justify-content-between align-items-center gap-2 gap-lg-1">
                                <div className="d-flex align-items-center gap-2 gap-lg-1 text-start flex-grow-1 min-w-0">
                                    <FontAwesomeIcon icon={faFilter} className="text-primary small flex-shrink-0" />
                                    <span className="fw-semibold">Categories</span>
                                    <Badge bg="secondary-subtle" className="text-secondary-emphasis rounded-pill flex-shrink-0">
                                        {categories.length}
                                    </Badge>
                                    {!isCategoriesExpanded && searchVideosOptions.categoryName && (
                                        <Badge bg="primary-subtle" className="text-primary-emphasis rounded-pill text-truncate d-lg-none">
                                            {searchVideosOptions.categoryName}
                                        </Badge>
                                    )}
                                </div>
                                <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary d-flex d-lg-none align-items-center gap-1"
                                        onClick={() => setIsCategoriesExpanded((prev) => !prev)}
                                        aria-expanded={isCategoriesExpanded}
                                        aria-controls="category-sidebar-body"
                                        title={isCategoriesExpanded ? "Collapse categories" : "Expand categories"}
                                    >
                                        <span className="small">{isCategoriesExpanded ? 'Collapse' : 'Expand'}</span>
                                        <FontAwesomeIcon icon={isCategoriesExpanded ? faChevronUp : faChevronDown} size="xs" />
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        title="Add category"
                                        aria-label="Add category"
                                        onClick={() => setShowAddCategoryModal(true)}
                                    >
                                        <FontAwesomeIcon icon={faPlus} />
                                    </button>
                                </div>
                            </Card.Header>

                            <div
                                id="category-sidebar-body"
                                className={`category-sidebar__body flex-column min-h-0 flex-grow-1 ${
                                    isCategoriesExpanded ? 'd-flex' : 'd-none d-lg-flex'
                                }`}
                            >
                                <div className="category-sidebar__search-wrapper position-relative">
                                    <FontAwesomeIcon icon={faSearch} className="category-sidebar__search-icon" />
                                    <input
                                        type="text"
                                        className="form-control form-control-sm category-sidebar__search-input"
                                        placeholder="Filter categories..."
                                        value={categorySearchQuery}
                                        onChange={(e) => setCategorySearchQuery(e.target.value)}
                                        aria-label="Filter categories"
                                    />
                                    {categorySearchQuery && (
                                        <button
                                            type="button"
                                            className="category-sidebar__search-clear"
                                            onClick={() => setCategorySearchQuery('')}
                                            aria-label="Clear category filter"
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    )}
                                </div>

                                <ListGroup variant="flush" className="category-sidebar__list">
                                    {!categorySearchQuery && (
                                        <ListGroup.Item
                                            action
                                            className={`category-sidebar__item d-flex justify-content-between align-items-center ${
                                                !searchVideosOptions.categoryName ? 'active' : ''
                                            }`}
                                            active={!searchVideosOptions.categoryName}
                                            onClick={() => handleSelectCategory(null)}
                                        >
                                            <div className="d-flex align-items-center gap-2 text-truncate">
                                                <FontAwesomeIcon icon={faLayerGroup} className="small opacity-75" />
                                                <span className="text-truncate">All videos</span>
                                            </div>
                                            <Badge
                                                className={
                                                    !searchVideosOptions.categoryName
                                                        ? 'text-bg-light text-primary rounded-pill'
                                                        : 'bg-secondary-subtle text-secondary-emphasis rounded-pill'
                                                }
                                            >
                                                {totalCategoryVideos}
                                            </Badge>
                                        </ListGroup.Item>
                                    )}

                                    {filteredCategories.map((category) => {
                                        const isActive = searchVideosOptions.categoryName === category.name;
                                        return (
                                            <ListGroup.Item
                                                key={category.name}
                                                action
                                                className={`category-sidebar__item d-flex justify-content-between align-items-center ${
                                                    isActive ? 'active' : ''
                                                }`}
                                                active={isActive}
                                                onClick={() => handleSelectCategory(category.name ?? null)}
                                            >
                                                <div className="d-flex align-items-center gap-2 text-truncate">
                                                    <FontAwesomeIcon
                                                        icon={isActive ? faFolderOpen : faFolder}
                                                        className="small opacity-75"
                                                    />
                                                    <span className="text-truncate" title={category.name ?? ''}>
                                                        {category.name}
                                                    </span>
                                                </div>
                                                <Badge
                                                    className={
                                                        isActive
                                                            ? 'text-bg-light text-primary rounded-pill'
                                                            : 'bg-secondary-subtle text-secondary-emphasis rounded-pill'
                                                    }
                                                >
                                                    {category.videoCount}
                                                </Badge>
                                            </ListGroup.Item>
                                        );
                                    })}

                                    {filteredCategories.length === 0 && (
                                        <div className="p-3 text-center text-body-secondary small">
                                            No categories match &ldquo;{categorySearchQuery}&rdquo;
                                        </div>
                                    )}
                                </ListGroup>
                            </div>
                        </Card>
                    </Col>

                    <Col lg={9} xl={10}>
                        <Stack gap={3}>
                            {/* Action Header & Controls */}
                            <div className="home-toolbar d-flex flex-wrap justify-content-between align-items-center gap-3">
                                <div className="d-flex align-items-center gap-2">
                                    <h2 className="h4 mb-0 fw-bold">
                                        {!searchVideosOptions.categoryName ? 'All videos' : searchVideosOptions.categoryName}
                                    </h2>
                                    <Badge bg="primary-subtle" className="text-primary-emphasis rounded-pill px-2 py-1">
                                        {videoPaginatedResults?.totalCount ?? 0}
                                    </Badge>
                                </div>

                                <div className="d-flex align-items-center gap-2 ms-auto flex-wrap">
                                    {/* Sort Dropdown */}
                                    <Dropdown onSelect={(key) => {
                                        const option = SORT_OPTIONS.find(o => o.label === key);
                                        if (option) handleSortChange(option);
                                    }}>
                                        <Dropdown.Toggle
                                            variant="outline-secondary"
                                            size="sm"
                                            id="sort-dropdown"
                                            className="d-flex align-items-center gap-2"
                                        >
                                            <FontAwesomeIcon icon={faArrowDownWideShort} />
                                            <span>{currentSort.label}</span>
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu align="end">
                                            {SORT_OPTIONS.map((option) => (
                                                <Dropdown.Item
                                                    key={option.label}
                                                    eventKey={option.label}
                                                    active={option.label === currentSort.label}
                                                >
                                                    {option.label}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>

                                    {/* Grid Column Density Switcher */}
                                    <ButtonGroup size="sm" aria-label="Grid layout density">
                                        <Button
                                            variant={columns === 3 ? "primary" : "outline-secondary"}
                                            onClick={() => handleColumnsChange(3)}
                                            title="Large grid (3 columns)"
                                            aria-label="3 columns"
                                        >
                                            <FontAwesomeIcon icon={faTableCellsLarge} />
                                        </Button>
                                        <Button
                                            variant={columns === 4 ? "primary" : "outline-secondary"}
                                            onClick={() => handleColumnsChange(4)}
                                            title="Standard grid (4 columns)"
                                            aria-label="4 columns"
                                        >
                                            <FontAwesomeIcon icon={faTableCells} />
                                        </Button>
                                        <Button
                                            variant={columns === 6 ? "primary" : "outline-secondary"}
                                            onClick={() => handleColumnsChange(6)}
                                            title="Compact grid (6 columns)"
                                            aria-label="6 columns"
                                        >
                                            <FontAwesomeIcon icon={faGrip} />
                                        </Button>
                                    </ButtonGroup>
                                </div>
                            </div>

                            {/* Active Filters Bar */}
                            {hasActiveFilters && (
                                <div className="d-flex align-items-center gap-2 flex-wrap p-2 px-3 bg-body-tertiary rounded-3 border">
                                    <span className="small text-body-secondary fw-semibold me-1">Active filters:</span>

                                    {searchVideosOptions.categoryName && (
                                        <span className="filter-chip">
                                            <FontAwesomeIcon icon={faFolder} size="xs" />
                                            <span>Category: {searchVideosOptions.categoryName}</span>
                                            <span
                                                role="button"
                                                className="filter-chip__remove"
                                                onClick={handleClearCategoryFilter}
                                                aria-label="Remove category filter"
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </span>
                                        </span>
                                    )}

                                    {searchVideosOptions.query && (
                                        <span className="filter-chip">
                                            <FontAwesomeIcon icon={faSearch} size="xs" />
                                            <span>Search: {searchVideosOptions.query}</span>
                                            <span
                                                role="button"
                                                className="filter-chip__remove"
                                                onClick={handleRemoveQuery}
                                                aria-label="Remove query filter"
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </span>
                                        </span>
                                    )}

                                    {searchVideosOptions.tags?.map((tag) => (
                                        <span key={tag} className="filter-chip">
                                            <span>#{tag}</span>
                                            <span
                                                role="button"
                                                className="filter-chip__remove"
                                                onClick={() => handleRemoveTagFromSearchOptions(tag)}
                                                aria-label={`Remove tag filter ${tag}`}
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </span>
                                        </span>
                                    ))}

                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="text-decoration-none p-0 ms-2 text-danger small fw-semibold"
                                        onClick={handleClearAllFilters}
                                    >
                                        Clear all
                                    </Button>
                                </div>
                            )}

                            {/* Videos Grid */}
                            <VideoResults
                                paginatedResults={videoPaginatedResults!}
                                apiClient={apiClient!}
                                onVideoUpdate={loadData}
                                onPageChange={(page) => handlePageChange(page)}
                                onVideoToggleTag={(video: VideoViewModel, bindingModel: ToggleVideoTagBindingModel) =>
                                    handleVideoToggleTag(video, bindingModel)
                                }
                                tags={tags}
                                columns={columns}
                            />
                        </Stack>
                    </Col>
                </Row>
            </Container>

            {apiClient && (
                <AddCategoryModal
                    show={showAddCategoryModal}
                    onHide={() => setShowAddCategoryModal(false)}
                    apiClient={apiClient}
                    onSuccess={(newCategoryName) => {
                        loadData();
                        handleSelectCategory(newCategoryName);
                    }}
                />
            )}
        </AppLayout>
    );
};
